import { z } from 'zod';

import { FannieMaeDelinquencyStatusCode } from '../investorReporting/FannieMae/FannieMaeDelinquencyFile';
import { FHATS264MortgageStatusCode } from '../investorReporting/FHA/FHATS264';
import { FreddieMacEDRActionCode } from '../investorReporting/FreddieMac/FreddieMacEDR';
import { GSEName } from './Loan';

export const NonPerformanceCode = z.enum([
  // Common between Fannie Mae and Freddie Mac
  'Trial Modification',
  'Forbearance',
  'Repayment Plan',
  'Reinstatement',
  'Probate',
  'Military Indulgence',
  'Contested/Litigated Foreclosure',
  'Foreclosure',
  'Assignment',
  'Second Lien Considerations',
  'Bankruptcy Plan Confirmed',
  'Foreclosure Sale Scheduled',
  'Breach Letter Sent',
  'Complete Borrower Response Package',

  // Fannie Mae only
  'Short Sale Approved/Offer Received',
  'Assumption',
  'Modification',
  'Charge‑off',
  'Mortgage Release',
  'Chapter 7 Asset Case',
  'Bankruptcy–Property Surrendered',
  'Chapter 12 Bankruptcy',
  'Chapter 7 Bankruptcy',
  'Chapter 11 Bankruptcy',
  'Chapter 13 Bankruptcy',
  'Drug Seizure',
  'Third‑Party Sale',
  'Short Sale Approved/Marketing Property',
  'Delinquent—No Action',
  'Refinance',
  'Veterans Affairs—Refund',
  'Chapter 13 Bankruptcy Plan - Post-Petition Period Utilize',
  'Judgment or Decree Entered',
  'Foreclosure Sale Continued',
  'Title Issue in Progress',
  'Pre-File Mediation/ Mediation',
  'Quality Right Party Contact',

  // Freddie Mac only
  'Government Seizure',
  'VA—No Bid',
  'VA—Refund',
  'VA—Buydown',
  'Repayment Plan in Review',
  'Bankruptcy Cramdown Scheduled',
  'Appeal of Loan Modification',
  'Date of First Legal Action',
  'BPO/Appraisal Obtained',
  'Bankruptcy Court Clearance Obtained/Stay Lifted',
  'Date of First QRPC',
  'Date of Last QRPC',
  'Date Advised Property Listed for Sale',
  'BPO Ordered',
  'Solicitation Letter Sent',
  'Workout in Review',
  'Short Sale in Review',
  'Ineligible for Short Sale',
  'Modification in Review',
  'Ineligible/Cancel Modification',
  'Payment Deferral Offer',

  // FHA-specific — NEW Aug 2025
  'Disaster Forbearance',
  'Modification Started 40 Year',
  'Outside of Waterfall Loan Modification',
  'Borrower Declined Loss Mitigation Consultation',
  'Borrower Participated in Loss Mitigation Consultation',
  'Combination Loan Modification and Partial Claim 40 Year',
  'Trial Payment Plan Standalone Partial Claim',
  'Trial Payment Plan Standalone Loan Modification',
  'Trial Payment Plan Combination Loan Modification/Partial Claim',
  'Trial Payment Plan Payment Supplement',
  'Trial Payment Plan Exempted Transfer',
  'Trial Payment Plan Outside of Waterfall Loan Modification',
  'Complete Loss Mitigation Request Received',
  'Loss Mitigation Request Denied',
  'State Mandated Delay',
  'Federal Law Mandated Delay',
  'Foreclosure Mediation',
  'Bankruptcy Discharged No Reaffirmation',
  'Bankruptcy Discharged Reaffirmation',
  'Reinstated Exempted Transfer',
  'Reinstated Exempted Transfer after Loss Mitigation',
]);

export type NonPerformanceCode = z.infer<typeof NonPerformanceCode>;

/**
 * Map from canonical `NonPerformanceCode` (human-readable) to corresponding Fannie Mae code.
 */
export const NonPerformanceCodeToFannieMae: Record<NonPerformanceCode, FannieMaeDelinquencyStatusCode | undefined> = {
  // Common
  'Trial Modification': 'BF',
  Forbearance: '09',
  'Repayment Plan': '12',
  Reinstatement: '20',
  Probate: '31',
  'Military Indulgence': '32',
  'Contested/Litigated Foreclosure': '33',
  Foreclosure: '43',
  Assignment: '49',
  'Second Lien Considerations': '61',
  'Bankruptcy Plan Confirmed': '69',
  'Foreclosure Sale Scheduled': '71',
  'Breach Letter Sent': '80',
  'Complete Borrower Response Package': 'H5',

  // Fannie-only
  'Short Sale Approved/Offer Received': '17',
  Assumption: '27',
  Modification: '28',
  'Charge‑off': '29',
  'Mortgage Release': '44',
  'Chapter 7 Asset Case': '3L',
  'Bankruptcy–Property Surrendered': '3M',
  'Chapter 12 Bankruptcy': '59',
  'Chapter 7 Bankruptcy': '65',
  'Chapter 11 Bankruptcy': '66',
  'Chapter 13 Bankruptcy': '67',
  'Drug Seizure': '24',
  'Third‑Party Sale': '30',
  'Short Sale Approved/Marketing Property': '15',
  'Delinquent—No Action': '42',
  Refinance: '26',
  'Veterans Affairs—Refund': '63',
  'Chapter 13 Bankruptcy Plan - Post-Petition Period Utilize': '69',
  'Judgment or Decree Entered': '94',
  'Foreclosure Sale Continued': '95',
  'Title Issue in Progress': 'BE',
  'Pre-File Mediation/ Mediation': 'BG',
  'Quality Right Party Contact': 'AW',

  // Freddie-only (no Fannie mapping)
  'Government Seizure': undefined,
  'VA—No Bid': undefined,
  'VA—Refund': undefined,
  'VA—Buydown': undefined,
  'Repayment Plan in Review': undefined,
  'Bankruptcy Cramdown Scheduled': undefined,
  'Appeal of Loan Modification': undefined,
  'Date of First Legal Action': undefined,
  'BPO/Appraisal Obtained': undefined,
  'Bankruptcy Court Clearance Obtained/Stay Lifted': undefined,
  'Date of First QRPC': undefined,
  'Date of Last QRPC': undefined,
  'Date Advised Property Listed for Sale': undefined,
  'BPO Ordered': undefined,
  'Solicitation Letter Sent': undefined,
  'Workout in Review': undefined,
  'Short Sale in Review': undefined,
  'Ineligible for Short Sale': undefined,
  'Modification in Review': undefined,
  'Ineligible/Cancel Modification': undefined,
  'Payment Deferral Offer': undefined,

  // FHA-only
  'Disaster Forbearance': undefined,
  'Modification Started 40 Year': undefined,
  'Outside of Waterfall Loan Modification': undefined,
  'Borrower Declined Loss Mitigation Consultation': undefined,
  'Borrower Participated in Loss Mitigation Consultation': undefined,
  'Combination Loan Modification and Partial Claim 40 Year': undefined,
  'Trial Payment Plan Standalone Partial Claim': undefined,
  'Trial Payment Plan Standalone Loan Modification': undefined,
  'Trial Payment Plan Combination Loan Modification/Partial Claim': undefined,
  'Trial Payment Plan Payment Supplement': undefined,
  'Trial Payment Plan Exempted Transfer': undefined,
  'Trial Payment Plan Outside of Waterfall Loan Modification': undefined,
  'Complete Loss Mitigation Request Received': undefined,
  'Loss Mitigation Request Denied': undefined,
  'State Mandated Delay': undefined,
  'Federal Law Mandated Delay': undefined,
  'Foreclosure Mediation': undefined,
  'Bankruptcy Discharged No Reaffirmation': undefined,
  'Bankruptcy Discharged Reaffirmation': undefined,
  'Reinstated Exempted Transfer': undefined,
  'Reinstated Exempted Transfer after Loss Mitigation': undefined,
};

/**
 * Map from canonical `NonPerformanceCode` (human-readable) to corresponding Freddie Mac EDR action code.
 */
export const NonPerformanceCodeToFreddieMacEDR: Record<NonPerformanceCode, FreddieMacEDRActionCode | undefined> = {
  // Common
  'Trial Modification': 'BF',
  Forbearance: '09',
  'Repayment Plan': '12',
  Reinstatement: '20',
  Probate: '31',
  'Military Indulgence': '32',
  'Contested/Litigated Foreclosure': '33',
  Foreclosure: '43',
  Assignment: '49', // HUD Assignment
  'Second Lien Considerations': '61',
  'Bankruptcy Plan Confirmed': '69',
  'Foreclosure Sale Scheduled': '71',
  'Breach Letter Sent': '80',
  'Complete Borrower Response Package': 'H5',

  // Fannie-only (no Freddie mapping)
  'Short Sale Approved/Offer Received': undefined,
  Assumption: undefined,
  Modification: undefined,
  'Charge‑off': undefined,
  'Mortgage Release': undefined,
  'Chapter 7 Asset Case': undefined,
  'Bankruptcy–Property Surrendered': undefined,
  'Chapter 12 Bankruptcy': '59',
  'Chapter 7 Bankruptcy': '65',
  'Chapter 11 Bankruptcy': '66',
  'Chapter 13 Bankruptcy': '67',
  'Drug Seizure': undefined,
  'Third‑Party Sale': undefined,
  'Short Sale Approved/Marketing Property': undefined,
  'Delinquent—No Action': undefined,
  Refinance: undefined,
  'Veterans Affairs—Refund': undefined,
  'Chapter 13 Bankruptcy Plan - Post-Petition Period Utilize': undefined,
  'Judgment or Decree Entered': undefined,
  'Foreclosure Sale Continued': undefined,
  'Title Issue in Progress': undefined,
  'Pre-File Mediation/ Mediation': undefined,
  'Quality Right Party Contact': undefined,

  // Freddie-only
  'Government Seizure': '24',
  'VA—No Bid': '62',
  'VA—Refund': '63',
  'VA—Buydown': '64',
  'Repayment Plan in Review': '11',
  'Bankruptcy Cramdown Scheduled': '35',
  'Appeal of Loan Modification': '38',
  'Date of First Legal Action': '68',
  'BPO/Appraisal Obtained': '74',
  'Bankruptcy Court Clearance Obtained/Stay Lifted': '76',
  'Date of First QRPC': 'AW',
  'Date of Last QRPC': 'AX',
  'Date Advised Property Listed for Sale': 'AY',
  'BPO Ordered': 'H3',
  'Solicitation Letter Sent': 'H4',
  'Workout in Review': 'HA',
  'Short Sale in Review': 'HB',
  'Ineligible for Short Sale': 'HC',
  'Modification in Review': 'HD',
  'Ineligible/Cancel Modification': 'HE',
  'Payment Deferral Offer': 'H6',

  // FHA-only (no Freddie mapping)
  'Disaster Forbearance': undefined,
  'Modification Started 40 Year': undefined,
  'Outside of Waterfall Loan Modification': undefined,
  'Borrower Declined Loss Mitigation Consultation': undefined,
  'Borrower Participated in Loss Mitigation Consultation': undefined,
  'Combination Loan Modification and Partial Claim 40 Year': undefined,
  'Trial Payment Plan Standalone Partial Claim': undefined,
  'Trial Payment Plan Standalone Loan Modification': undefined,
  'Trial Payment Plan Combination Loan Modification/Partial Claim': undefined,
  'Trial Payment Plan Payment Supplement': undefined,
  'Trial Payment Plan Exempted Transfer': undefined,
  'Trial Payment Plan Outside of Waterfall Loan Modification': undefined,
  'Complete Loss Mitigation Request Received': undefined,
  'Loss Mitigation Request Denied': undefined,
  'State Mandated Delay': undefined,
  'Federal Law Mandated Delay': undefined,
  'Foreclosure Mediation': undefined,
  'Bankruptcy Discharged No Reaffirmation': undefined,
  'Bankruptcy Discharged Reaffirmation': undefined,
  'Reinstated Exempted Transfer': undefined,
  'Reinstated Exempted Transfer after Loss Mitigation': undefined,
};

/**
 * Map from canonical `NonPerformanceCode` (human-readable) to corresponding FHA TS264 DDS mortgage status code.
 * Only codes that have a meaningful FHA mapping are included; the rest are `undefined`.
 */
export const NonPerformanceCodeToFHATS264: Record<NonPerformanceCode, FHATS264MortgageStatusCode | undefined> = {
  // Common
  'Trial Modification': '08',
  Forbearance: '06',
  'Repayment Plan': '12',
  Reinstatement: '20', // Account Reinstated — Current
  Probate: '31',
  'Military Indulgence': '32',
  'Contested/Litigated Foreclosure': '33',
  Foreclosure: '68', // First Legal Action / Foreclosure Initiated
  Assignment: '10', // Partial Claim
  'Second Lien Considerations': undefined,
  'Bankruptcy Plan Confirmed': '69',
  'Foreclosure Sale Scheduled': undefined,
  'Breach Letter Sent': undefined,
  'Complete Borrower Response Package': undefined,

  // Fannie Mae-only
  'Short Sale Approved/Offer Received': '09', // Pre-Foreclosure Sale Approved
  Assumption: undefined,
  Modification: '28',
  'Charge‑off': undefined,
  'Mortgage Release': undefined,
  'Chapter 7 Asset Case': undefined,
  'Bankruptcy–Property Surrendered': undefined,
  'Chapter 12 Bankruptcy': '59',
  'Chapter 7 Bankruptcy': '65',
  'Chapter 11 Bankruptcy': '66',
  'Chapter 13 Bankruptcy': '67',
  'Drug Seizure': undefined,
  'Third‑Party Sale': undefined,
  'Short Sale Approved/Marketing Property': undefined,
  'Delinquent—No Action': '42',
  Refinance: undefined,
  'Veterans Affairs—Refund': undefined,
  'Chapter 13 Bankruptcy Plan - Post-Petition Period Utilize': '69',
  'Judgment or Decree Entered': undefined,
  'Foreclosure Sale Continued': undefined,
  'Title Issue in Progress': undefined,
  'Pre-File Mediation/ Mediation': undefined,
  'Quality Right Party Contact': undefined,

  // Freddie Mac-only (no FHA mapping)
  'Government Seizure': undefined,
  'VA—No Bid': undefined,
  'VA—Refund': undefined,
  'VA—Buydown': undefined,
  'Repayment Plan in Review': undefined,
  'Bankruptcy Cramdown Scheduled': undefined,
  'Appeal of Loan Modification': undefined,
  'Date of First Legal Action': '68',
  'BPO/Appraisal Obtained': undefined,
  'Bankruptcy Court Clearance Obtained/Stay Lifted': undefined,
  'Date of First QRPC': undefined,
  'Date of Last QRPC': undefined,
  'Date Advised Property Listed for Sale': undefined,
  'BPO Ordered': undefined,
  'Solicitation Letter Sent': undefined,
  'Workout in Review': undefined,
  'Short Sale in Review': undefined,
  'Ineligible for Short Sale': undefined,
  'Modification in Review': undefined,
  'Ineligible/Cancel Modification': undefined,
  'Payment Deferral Offer': undefined,

  // FHA-specific — NEW Aug 2025
  // Delinquency Workouts (AL)
  'Disaster Forbearance': '23',
  'Modification Started 40 Year': '40',
  'Outside of Waterfall Loan Modification': '60',
  'Borrower Declined Loss Mitigation Consultation': '80',
  'Borrower Participated in Loss Mitigation Consultation': '81',
  'Combination Loan Modification and Partial Claim 40 Year': '93',
  'Trial Payment Plan Standalone Partial Claim': '3C',
  'Trial Payment Plan Standalone Loan Modification': '3D',
  'Trial Payment Plan Combination Loan Modification/Partial Claim': '3E',
  'Trial Payment Plan Payment Supplement': '3F',
  'Trial Payment Plan Exempted Transfer': '3G',
  'Trial Payment Plan Outside of Waterfall Loan Modification': '3H',
  'Complete Loss Mitigation Request Received': 'BB',
  'Loss Mitigation Request Denied': 'BD',
  // NOTE: DDS '05' (Repayment Plan) and '07' (Forbearance General) are new codes but overlap
  // with existing '12' (Repayment Plan) and '06' (Formal Forbearance). Our internal model
  // does not currently distinguish between formal and general forbearance, or between
  // the two repayment plan codes. These DDS codes exist in FHATS264MortgageStatusCode
  // but have no distinct NonPerformanceCode mapping yet.

  // Account on Foreclosure Hold (new category — codes moved from Account in Foreclosure)
  'State Mandated Delay': '95',
  'Federal Law Mandated Delay': '96',

  // Account in Foreclosure (AF)
  'Foreclosure Mediation': '2Z',

  // Account in Bankruptcy (AB)
  'Bankruptcy Discharged No Reaffirmation': '75',
  'Bankruptcy Discharged Reaffirmation': '79',

  // Account Reinstated (AR)
  'Reinstated Exempted Transfer': '89',
  'Reinstated Exempted Transfer after Loss Mitigation': '99',
};

export const NonPerformanceCodeToGenericCode: Record<NonPerformanceCode, string | undefined> = {
  // Common
  'Trial Modification': 'Trial Modification',
  Forbearance: 'Forbearance',
  'Repayment Plan': 'Repayment Plan',
  Reinstatement: 'Reinstatement',
  Probate: 'Probate',
  'Military Indulgence': 'Military Indulgence',
  'Contested/Litigated Foreclosure': 'Contested/Litigated Foreclosure',
  Foreclosure: 'Foreclosure',
  Assignment: 'Assignment',
  'Second Lien Considerations': 'Second Lien Considerations',
  'Bankruptcy Plan Confirmed': 'Bankruptcy Plan Confirmed',
  'Foreclosure Sale Scheduled': 'Foreclosure Sale Scheduled',
  'Breach Letter Sent': 'Breach Letter Sent',
  'Quality Right Party Contact': 'Quality Right Party Contact',
  'Complete Borrower Response Package': 'Complete Borrower Response Package',
  'Short Sale Approved/Offer Received': 'Short Sale Approved/Offer Received',
  Assumption: 'Assumption',
  Modification: 'Modification',
  'Charge‑off': 'Charge‑off',
  'Mortgage Release': 'Mortgage Release',
  'Chapter 7 Asset Case': 'Chapter 7 Asset Case',
  'Chapter 7 Bankruptcy': 'Chapter 7 Bankruptcy',
  'Bankruptcy–Property Surrendered': 'Bankruptcy–Property Surrendered',
  'Chapter 12 Bankruptcy': 'Chapter 12 Bankruptcy',
  'Chapter 11 Bankruptcy': 'Chapter 11 Bankruptcy',
  'Chapter 13 Bankruptcy': 'Chapter 13 Bankruptcy',
  'Drug Seizure': 'Drug Seizure',
  'Third‑Party Sale': 'Third‑Party Sale',
  'Short Sale Approved/Marketing Property': 'Short Sale Approved/Marketing Property',
  'Delinquent—No Action': 'Delinquent—No Action',
  Refinance: 'Refinance',
  'Veterans Affairs—Refund': 'Veterans Affairs—Refund',
  'Chapter 13 Bankruptcy Plan - Post-Petition Period Utilize':
    'Chapter 13 Bankruptcy Plan - Post-Petition Period Utilize',
  'Judgment or Decree Entered': 'Judgment or Decree Entered',
  'Foreclosure Sale Continued': 'Foreclosure Sale Continued',
  'Title Issue in Progress': 'Title Issue in Progress',
  'Pre-File Mediation/ Mediation': 'Pre-File Mediation/ Mediation',

  // Freddie-only (no Fannie mapping)
  'Government Seizure': 'Government Seizure',
  'VA—No Bid': 'VA—No Bid',
  'VA—Refund': 'VA—Refund',
  'VA—Buydown': 'VA—Buydown',
  'Repayment Plan in Review': 'Repayment Plan in Review',
  'Bankruptcy Cramdown Scheduled': 'Bankruptcy Cramdown Scheduled',
  'Appeal of Loan Modification': 'Appeal of Loan Modification',
  'Date of First Legal Action': 'Date of First Legal Action',
  'BPO/Appraisal Obtained': 'BPO/Appraisal Obtained',
  'Bankruptcy Court Clearance Obtained/Stay Lifted': 'Bankruptcy Court Clearance Obtained/Stay Lifted',
  'Date of First QRPC': undefined,
  'Date of Last QRPC': undefined,
  'Date Advised Property Listed for Sale': undefined,
  'BPO Ordered': 'BPO Ordered',
  'Solicitation Letter Sent': 'Solicitation Letter Sent',
  'Workout in Review': 'Workout Plan in Review',
  'Short Sale in Review': 'Short Sale in Review',
  'Ineligible for Short Sale': 'Ineligible for Short Sale',
  'Modification in Review': 'Modification in Review',
  'Ineligible/Cancel Modification': 'Ineligible/Cancel Modification',
  'Payment Deferral Offer': 'Payment Deferral Offer',

  // FHA-only
  'Disaster Forbearance': 'Disaster Forbearance',
  'Modification Started 40 Year': 'Modification Started 40 Year',
  'Outside of Waterfall Loan Modification': 'Outside of Waterfall Loan Modification',
  'Borrower Declined Loss Mitigation Consultation': 'Borrower Declined Loss Mitigation Consultation',
  'Borrower Participated in Loss Mitigation Consultation': 'Borrower Participated in Loss Mitigation Consultation',
  'Combination Loan Modification and Partial Claim 40 Year': 'Combination Loan Modification and Partial Claim 40 Year',
  'Trial Payment Plan Standalone Partial Claim': 'Trial Payment Plan Standalone Partial Claim',
  'Trial Payment Plan Standalone Loan Modification': 'Trial Payment Plan Standalone Loan Modification',
  'Trial Payment Plan Combination Loan Modification/Partial Claim':
    'Trial Payment Plan Combination Loan Modification/Partial Claim',
  'Trial Payment Plan Payment Supplement': 'Trial Payment Plan Payment Supplement',
  'Trial Payment Plan Exempted Transfer': 'Trial Payment Plan Exempted Transfer',
  'Trial Payment Plan Outside of Waterfall Loan Modification':
    'Trial Payment Plan Outside of Waterfall Loan Modification',
  'Complete Loss Mitigation Request Received': 'Complete Loss Mitigation Request Received',
  'Loss Mitigation Request Denied': 'Loss Mitigation Request Denied',
  'State Mandated Delay': 'State Mandated Delay',
  'Federal Law Mandated Delay': 'Federal Law Mandated Delay',
  'Foreclosure Mediation': 'Foreclosure Mediation',
  'Bankruptcy Discharged No Reaffirmation': 'Bankruptcy Discharged No Reaffirmation',
  'Bankruptcy Discharged Reaffirmation': 'Bankruptcy Discharged Reaffirmation',
  'Reinstated Exempted Transfer': 'Reinstated Exempted Transfer',
  'Reinstated Exempted Transfer after Loss Mitigation': 'Reinstated Exempted Transfer after Loss Mitigation',
};

export const NonPerformanceCodeMappingByInvestor: Record<
  GSEName | 'other',
  Record<NonPerformanceCode, string | undefined>
> = {
  FNMA: NonPerformanceCodeToFannieMae,
  FHLMC: NonPerformanceCodeToFreddieMacEDR,
  GNMA: NonPerformanceCodeToGenericCode,
  FHA: NonPerformanceCodeToFHATS264,
  other: NonPerformanceCodeToGenericCode,
};

/**
 * FNMA code priorities
 * */
const fnmaPriority1Codes = new Set<NonPerformanceCode>([
  'Trial Modification',
  'Forbearance',
  'Short Sale Approved/Offer Received',
  'Repayment Plan',
  'Assumption',
  'Modification',
  'Charge‑off',
  'Military Indulgence',
  'Mortgage Release',
]);

const fnmaPriority2Codes = new Set<NonPerformanceCode>(['Complete Borrower Response Package']);

const fnmaPriority3Codes = new Set<NonPerformanceCode>([
  'Chapter 7 Asset Case',
  'Bankruptcy–Property Surrendered',
  'Chapter 12 Bankruptcy',
  'Chapter 7 Bankruptcy',
  'Chapter 11 Bankruptcy',
  'Chapter 13 Bankruptcy',
  'Chapter 13 Bankruptcy Plan - Post-Petition Period Utilize',
]);

const fnmaPriority4Codes = new Set<NonPerformanceCode>([
  'Reinstatement',
  'Drug Seizure',
  'Third‑Party Sale',
  'Probate',
  'Contested/Litigated Foreclosure',
  'Foreclosure',
  'Second Lien Considerations',
  'Veterans Affairs—Refund',
  'Foreclosure Sale Scheduled',
  'Judgment or Decree Entered',
  'Foreclosure Sale Continued',
  'Title Issue in Progress',
  'Pre-File Mediation/ Mediation',
]);

const fnmaPriority5Codes = new Set<NonPerformanceCode>([
  'Quality Right Party Contact',
  'Short Sale Approved/Marketing Property',
  'Delinquent—No Action',
  'Breach Letter Sent',
]);

const fnmaPriority6Codes = new Set<NonPerformanceCode>(['Refinance', 'Assignment']);

export const fnmaCodePriorities = new Map<number, Set<NonPerformanceCode>>([
  [1, fnmaPriority1Codes],
  [2, fnmaPriority2Codes],
  [3, fnmaPriority3Codes],
  [4, fnmaPriority4Codes],
  [5, fnmaPriority5Codes],
  [6, fnmaPriority6Codes],
]);

export const FORECLOSURE_CODES = new Set<NonPerformanceCode>([
  'Foreclosure',
  'Date of First Legal Action',
  'Foreclosure Sale Scheduled',
  'Contested/Litigated Foreclosure',
  'Foreclosure Sale Continued',
]);

export const BANKRUPTCY_CODES = new Set<NonPerformanceCode>([
  'Chapter 7 Bankruptcy',
  'Chapter 11 Bankruptcy',
  'Chapter 12 Bankruptcy',
  'Chapter 13 Bankruptcy',
  'Chapter 7 Asset Case',
  'Bankruptcy Plan Confirmed',
  'Bankruptcy–Property Surrendered',
  'Bankruptcy Court Clearance Obtained/Stay Lifted',
  'Bankruptcy Cramdown Scheduled',
]);
